import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  GraduationCap, 
  Play, 
  Clock, 
  CheckCircle, 
  BookOpen,
  Loader2,
  ChevronDown,
  ChevronRight,
  Compass,
  UserCheck,
  Mic,
  Crown,
  Puzzle,
  Rocket,
  DollarSign,
  Cpu,
  Target,
  Lightbulb,
  Sparkles,
  Lock
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { toast } from 'sonner';
import UpgradePrompt from '../components/UpgradePrompt';
import { 
  LEARNING_MODULES, 
  getTypeBadgeColor, 
  getModuleColorClasses,
  getEmbedUrl 
} from '../data/learningModules';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  'compass': Compass,
  'user-check': UserCheck,
  'mic': Mic,
  'crown': Crown,
  'puzzle': Puzzle,
  'rocket': Rocket,
  'dollar-sign': DollarSign,
  'cpu': Cpu
};

const LearningSuite = () => {
  const { token } = useAuth();
  const { hasFullAccess, isPreviewMode, loading: subLoading } = useSubscription();
  const [expandedModule, setExpandedModule] = useState(1);
  const [completedItems, setCompletedItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    fetchProgress();
  }, [token]);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/learning/module-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedItems(response.data.completed_items || {});
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const playVideo = (item) => {
    if (!hasFullAccess) {
      toast.error('Activate your membership to watch full lessons');
      return;
    }
    const embedUrl = getEmbedUrl(item.videoUrl);
    if (embedUrl) {
      setActiveVideo({ ...item, embedUrl });
    } else {
      // Open in new tab for channel links
      window.open(item.videoUrl, '_blank');
    }
  };

  const markItemComplete = async (itemId) => {
    if (!hasFullAccess) {
      toast.error('Activate your membership to track progress');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/learning/complete-item/${itemId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedItems(prev => ({ ...prev, [itemId]: true }));
      toast.success('Learning item completed!');
    } catch (error) {
      toast.error('Failed to mark as complete');
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
    setActiveVideo(null);
  };

  // Calculate progress
  const totalItems = LEARNING_MODULES.reduce((sum, m) => sum + m.items.length, 0);
  const completedCount = Object.keys(completedItems).filter(k => completedItems[k]).length;
  const overallProgress = Math.round((completedCount / totalItems) * 100);

  const getModuleProgress = (module) => {
    const completed = module.items.filter(item => completedItems[item.id]).length;
    return Math.round((completed / module.items.length) * 100);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="learning-suite-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">
            Atlas Career Solutions Learning Suite
          </h1>
          <p className="text-navy-600 dark:text-navy-300">
            Professional development modules for career success
          </p>
        </div>

        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Lock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  <div>
                    <p className="font-medium text-navy-900 dark:text-white">Preview Mode</p>
                    <p className="text-sm text-navy-600 dark:text-navy-300">
                      You can browse module outlines. Activate membership to watch lessons and track progress.
                    </p>
                  </div>
                </div>
                <Link to="/membership">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Unlock Full Access
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Overview */}
        <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-navy-900 dark:text-white">
                    Your Learning Journey
                  </h2>
                  <p className="text-navy-500 dark:text-navy-400">
                    {completedCount} of {totalItems} learning items completed
                  </p>
                </div>
              </div>
              <div className="flex-1 max-w-md">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-navy-600 dark:text-navy-400">Overall Progress</span>
                  <span className="font-semibold text-navy-900 dark:text-white">{overallProgress}%</span>
                </div>
                <Progress value={overallProgress} className="h-3" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy-900 dark:text-white">8</p>
                <p className="text-xs text-navy-500 dark:text-navy-400">Modules</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                <Play className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy-900 dark:text-white">{totalItems}</p>
                <p className="text-xs text-navy-500 dark:text-navy-400">Learning Items</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy-900 dark:text-white">{completedCount}</p>
                <p className="text-xs text-navy-500 dark:text-navy-400">Completed</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy-900 dark:text-white">{totalItems - completedCount}</p>
                <p className="text-xs text-navy-500 dark:text-navy-400">Remaining</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Video Player Modal */}
        {activeVideo && (
          <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Badge className={getTypeBadgeColor(activeVideo.type)}>{activeVideo.type}</Badge>
                  <p className="text-sm text-navy-500 dark:text-navy-400 mt-1">{activeVideo.subtitle}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setActiveVideo(null)}>
                  Close
                </Button>
              </div>
              <div className="aspect-video bg-navy-900 rounded-lg overflow-hidden">
                <iframe
                  src={activeVideo.embedUrl}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Learning Video"
                />
              </div>
              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-navy-900 dark:text-white text-sm">Action Step</p>
                    <p className="text-navy-600 dark:text-navy-300 text-sm">{activeVideo.actionStep}</p>
                  </div>
                </div>
              </div>
              {!completedItems[activeVideo.id] && (
                <Button 
                  className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => markItemComplete(activeVideo.id)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Complete
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Modules List */}
        <div className="space-y-4">
          {LEARNING_MODULES.map((module) => {
            const Icon = iconMap[module.icon] || BookOpen;
            const colors = getModuleColorClasses(module.color);
            const moduleProgress = getModuleProgress(module);
            const isExpanded = expandedModule === module.id;

            return (
              <Card 
                key={module.id} 
                className={`border-navy-100 dark:border-navy-700 dark:bg-navy-800 overflow-hidden transition-all ${
                  isExpanded ? 'ring-2 ring-amber-500/50' : ''
                }`}
              >
                {/* Module Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-navy-50 dark:hover:bg-navy-700/50 transition-colors"
                  onClick={() => toggleModule(module.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs font-medium">
                          {module.number}
                        </Badge>
                        {moduleProgress === 100 && (
                          <Badge className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-heading font-semibold text-navy-900 dark:text-white">
                        {module.title}
                      </h3>
                      <p className="text-sm text-navy-500 dark:text-navy-400">{module.subheading}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-navy-900 dark:text-white">{moduleProgress}%</p>
                        <p className="text-xs text-navy-500 dark:text-navy-400">Complete</p>
                      </div>
                      <div className="w-24">
                        <Progress value={moduleProgress} className="h-2" />
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-navy-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-navy-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-navy-100 dark:border-navy-700">
                    {/* Learning Objective */}
                    <div className="p-4 bg-navy-50 dark:bg-navy-900/50 border-b border-navy-100 dark:border-navy-700">
                      <div className="flex items-start gap-2">
                        <Target className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                        <div>
                          <p className="font-medium text-navy-900 dark:text-white text-sm">Learning Objective</p>
                          <p className="text-navy-600 dark:text-navy-300 text-sm">{module.learningObjective}</p>
                        </div>
                      </div>
                    </div>

                    {/* Learning Items */}
                    <div className="divide-y divide-navy-100 dark:divide-navy-700">
                      {module.items.map((item, idx) => {
                        const isCompleted = completedItems[item.id];
                        
                        return (
                          <div 
                            key={item.id}
                            className={`p-4 ${isCompleted ? 'bg-emerald-50/50 dark:bg-emerald-900/20' : ''}`}
                          >
                            <div className="flex items-start gap-4">
                              {/* Item Number */}
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isCompleted 
                                  ? 'bg-emerald-100 dark:bg-emerald-900/50' 
                                  : 'bg-navy-100 dark:bg-navy-700'
                              }`}>
                                {isCompleted ? (
                                  <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                ) : (
                                  <span className="text-sm font-medium text-navy-600 dark:text-navy-300">{idx + 1}</span>
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={getTypeBadgeColor(item.type)}>
                                    {item.type}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-navy-500 dark:text-navy-400 mb-2">
                                  <Clock className="w-4 h-4" />
                                  <span>{item.subtitle}</span>
                                </div>
                                
                                {/* Action Step */}
                                <div className="p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800 mb-3">
                                  <div className="flex items-start gap-2">
                                    <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-0.5">Action Step</p>
                                      <p className="text-sm text-navy-700 dark:text-navy-200">{item.actionStep}</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex items-center gap-2">
                                  <Button 
                                    size="sm" 
                                    className={hasFullAccess ? "bg-amber-600 hover:bg-amber-700" : "bg-navy-400 hover:bg-navy-500"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      playVideo(item);
                                    }}
                                  >
                                    {hasFullAccess ? (
                                      <Play className="w-4 h-4 mr-1" />
                                    ) : (
                                      <Lock className="w-4 h-4 mr-1" />
                                    )}
                                    {hasFullAccess ? 'Watch' : 'Locked'}
                                  </Button>
                                  {!isCompleted && hasFullAccess && (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      className="dark:border-navy-600 dark:text-white dark:hover:bg-navy-700"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        markItemComplete(item.id);
                                      }}
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Complete
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LearningSuite;
